import type { TaskTypeDef } from '@scrambl/shared'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTaskTypesForContext } from '@/hooks/use-task-types'
import { cn } from '@/lib/utils'
import { TaskTypeIcon } from './TaskTypeIcon'

const DEFAULT_TASK_TYPE_VALUE = '__default_task_type__'
const DEFAULT_TASK_TYPE_KEY = 'task'

interface TaskTypeSelectProps {
  projectId?: string | null
  value?: string | null
  onValueChange: (value: string | null) => void
  disabled?: boolean
  className?: string
  triggerClassName?: string
  placeholder?: string
  defaultLabel?: string
  defaultTypeDefId?: string | null
  includeDefaultOption?: boolean
  triggerDisplay?: 'label' | 'icon'
  /**
   * An extra choice listed above the types, for callers whose selection cannot
   * be expressed as a single type (e.g. bulk promote's "match each ticket").
   */
  extraOption?: { value: string; label: string } | null
}

function typeLabel(type: TaskTypeDef) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <TaskTypeIcon
        icon={type.icon}
        color={type.color}
        displayStyle={type.displayStyle}
        name={type.name}
        label={type.name}
      />
      <span className="truncate">{type.name}</span>
    </span>
  )
}

export function TaskTypeSelect({
  projectId,
  value,
  onValueChange,
  disabled,
  className,
  triggerClassName,
  placeholder = 'Task type',
  defaultLabel = 'Default type',
  defaultTypeDefId,
  includeDefaultOption = true,
  triggerDisplay = 'label',
  extraOption = null,
}: TaskTypeSelectProps) {
  const { data = [], isLoading } = useTaskTypesForContext(projectId, !disabled)
  const taskTypes = data.filter(type => type.isEnabled && !type.isArchived)
  const isExtraSelected = Boolean(extraOption && value === extraOption.value)
  const selectedType = taskTypes.find(type => type.id === value)
  const defaultType = taskTypes.find(type => type.id === defaultTypeDefId)
  const fallbackDefaultType =
    taskTypes.find(type => type.key === DEFAULT_TASK_TYPE_KEY) ?? taskTypes[0]
  const displayType = selectedType ?? defaultType ?? fallbackDefaultType
  const displayLabel = selectedType
    ? selectedType.name
    : defaultType
      ? `Default: ${defaultType.name}`
      : fallbackDefaultType
        ? `Default: ${fallbackDefaultType.name}`
        : defaultLabel

  return (
    <Select
      value={value || DEFAULT_TASK_TYPE_VALUE}
      onValueChange={next =>
        onValueChange(next === DEFAULT_TASK_TYPE_VALUE ? null : next)
      }
      disabled={
        disabled || isLoading || (taskTypes.length === 0 && !extraOption)
      }
    >
      <SelectTrigger
        className={cn(
          'min-w-0 justify-start gap-2',
          // The trigger's default `[&>span]:line-clamp-1` forces the value span
          // to a clipped -webkit-box, cutting off the h-4 type badge. Truncation
          // is handled by the inner name span, so opt this select out.
          '[&>span]:line-clamp-none',
          triggerClassName,
          triggerDisplay === 'icon' &&
            'h-6 w-6 justify-center gap-0 rounded border-0 bg-transparent p-0.5 shadow-none hover:border-transparent hover:bg-accent focus:border-transparent [&>svg]:hidden',
        )}
        title={displayLabel}
      >
        {triggerDisplay === 'icon' ? (
          <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
            {displayType ? (
              <TaskTypeIcon
                icon={displayType.icon}
                color={displayType.color}
                displayStyle={displayType.displayStyle}
                name={displayType.name}
                label={displayLabel}
              />
            ) : (
              <TaskTypeIcon label={defaultLabel} />
            )}
          </div>
        ) : (
          <SelectValue placeholder={placeholder}>
            {isExtraSelected
              ? extraOption?.label
              : selectedType
              ? typeLabel(selectedType)
              : defaultType
                ? typeLabel(defaultType)
                : fallbackDefaultType
                  ? typeLabel(fallbackDefaultType)
                  : defaultLabel}
          </SelectValue>
        )}
      </SelectTrigger>
      <SelectContent className={cn('max-h-72', className)}>
        {includeDefaultOption ? (
          <SelectItem value={DEFAULT_TASK_TYPE_VALUE}>
            {defaultType ? (
              <span className="flex min-w-0 items-center gap-2">
                {typeLabel(defaultType)}
                <span className="text-xs text-muted-foreground">(default)</span>
              </span>
            ) : (
              defaultLabel
            )}
          </SelectItem>
        ) : null}
        {extraOption ? (
          <SelectItem value={extraOption.value}>{extraOption.label}</SelectItem>
        ) : null}
        {taskTypes.map(type => (
          <SelectItem key={type.id} value={type.id}>
            {typeLabel(type)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
